import { createFileRoute, redirect } from '@tanstack/react-router'
import { fallback, zodValidator } from '@tanstack/zod-adapter'
import { z } from 'zod'

import type { InviteEntityTypeEnum } from '@/api/codegen'
import { redirectInvite } from '@/api/codegen/sdk.gen'
import { useInviteRedirectStore } from '@/stores/inviteRedirect.store'
import { withCompanyIdInRedirectUrl } from '@/utils/companyPath'

export const InvitePage = () => null

const INVITE_TYPE_SCHEMA = z.enum([
  'company',
  'space',
  'project',
  'collaborator',
])

const DEFAULT_SEARCH = {
  type: undefined,
  id: undefined,
  projectId: undefined,
} as const

const SEARCH_SCHEMA = z.object({
  type: fallback(INVITE_TYPE_SCHEMA.optional(), DEFAULT_SEARCH.type).optional(),
  id: fallback(
    z.union([z.string(), z.number()]).transform(String).optional(),
    DEFAULT_SEARCH.id,
  ).optional(),
  projectId: fallback(
    z.union([z.string(), z.number()]).transform(String).optional(),
    DEFAULT_SEARCH.projectId,
  ).optional(),
})

export const Route = createFileRoute('/_auth/invite')({
  validateSearch: zodValidator(SEARCH_SCHEMA),
  beforeLoad: async ({ search }) => {
    const isInvalidEntityId =
      (search.type === 'company' ||
        search.type === 'space' ||
        search.type === 'project') &&
      !search.id

    const isInvalidProjectId =
      search.type === 'collaborator' && !search.projectId

    const isInvalidInvite =
      !search.type || isInvalidEntityId || isInvalidProjectId

    if (isInvalidInvite) {
      useInviteRedirectStore.getState().setErrorMessage('Invalid invite link')

      throw redirect({
        to: '/',
        replace: true,
      })
    }

    const result = await redirectInvite({
      query: {
        type: search.type as InviteEntityTypeEnum,
        id: search.id,
        projectId: search.projectId,
      },
      throwOnError: false,
    })

    if (!result.response.ok || !result.data) {
      useInviteRedirectStore
        .getState()
        .setErrorMessage('Invalid or expired invite')

      throw redirect({
        to: '/',
        replace: true,
      })
    }

    throw redirect({
      to: result.data.companyId
        ? withCompanyIdInRedirectUrl(result.data.url, result.data.companyId)
        : result.data.url,
      replace: true,
    })
  },
  component: InvitePage,
})
